/**
 * SubscriptionSidebarCards — Paket C (premium-checkout-redesign)
 *
 * Ausgelagert aus SubscriptionPage.tsx (Dateigrößen-Limit): die rechte
 * Spalte der aktiven Abo-Ansicht — Zahlungsmethode (BillingCard) und
 * Kündigen (CancelCard).
 */
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DemoGuardedButton } from '../../components/DemoGuardedButton'
import type { Subscription } from './useSubscription'
import type { Plan } from './useSubscriptionPlans'
import { ChangePaymentMethodModal } from './ChangePaymentMethodModal'
import { formatSubscriptionDate } from './subscriptionUtils'
import { planQuotas, PLAN_QUOTA_FALLBACK, type PlanQuota } from '../premium/planQuotas'

// --- Zahlungsmethode-Karte (Prototyp: rechte Spalte, oben) ---

export function BillingCard({
  subscription,
  onUpdatePaymentMethod,
  onPaymentMethodUpdated,
}: {
  subscription: Subscription
  onUpdatePaymentMethod: (stripeToken: string) => Promise<void>
  onPaymentMethodUpdated: () => void
}) {
  const { t } = useTranslation('subscription')
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const periodEnd = formatSubscriptionDate(subscription.current_period_end)
  const pm = subscription.payment_method

  return (
    <div className="card card-pad" data-testid="billing-card">
      <div className="spread">
        <h3 style={{ margin: 0, fontSize: 14 }}>{t('billing.title')}</h3>
        <button
          type="button"
          className="btn btn-sm"
          onClick={() => setShowPaymentModal(true)}
          data-testid="change-payment-btn"
        >
          {t('billing.changeButton')}
        </button>
      </div>

      {pm ? (
        <div className="pm-card" style={{ marginTop: 14 }}>
          <div className="pm-brand">{pm.brand.toUpperCase()}</div>
          <div>
            <b style={{ fontSize: 13 }}>{t('billing.cardLine', { brand: pm.brand.toUpperCase(), last4: pm.last4 })}</b>
            <div className="muted" style={{ fontSize: 11 }}>
              {t('billing.validUntil', {
                month: String(pm.exp_month).padStart(2, '0'),
                year: String(pm.exp_year).slice(-2),
              })}
              {' · '}
              <span className={subscription.cancel_at_period_end ? 'c-danger' : 'c-success'}>
                {subscription.cancel_at_period_end ? t('billing.autoRenewCancelled') : t('billing.autoRenewActive')}
              </span>
            </div>
          </div>
        </div>
      ) : (
        <>
          <p className="muted" style={{ fontSize: 13, marginTop: 14 }}>{t('billing.methodNone')}</p>
          <button
            type="button"
            className="btn btn-sm"
            style={{ marginTop: 8 }}
            onClick={() => setShowPaymentModal(true)}
            data-testid="add-payment-btn"
          >
            {t('billing.addButton')}
          </button>
        </>
      )}

      {subscription.auto_renew === true && (
        <p className="muted" style={{ fontSize: 11, margin: '12px 0 0' }}>
          {t('billing.nextChargeNote', { date: periodEnd })}
        </p>
      )}

      {showPaymentModal && (
        <ChangePaymentMethodModal
          onSuccess={() => {
            setShowPaymentModal(false)
            onPaymentMethodUpdated()
          }}
          onClose={() => setShowPaymentModal(false)}
          updatePaymentMethod={onUpdatePaymentMethod}
        />
      )}
    </div>
  )
}

// --- Kündigen-Karte (Prototyp: rechte Spalte, unten) ---

// Issue #123: "1 Mobile / 2 IoT" stand fest im Übersetzungstext, obwohl der
// Tarifkatalog fürs Free-Abo 1 Mobile / 3 IoT durchsetzt (Backend:
// Authentication::SubscriptionService::FREE_MOBILE_HOTSPOT_LIMIT/
// FREE_IOT_HOTSPOT_LIMIT). Die Zahlen kommen jetzt aus den Plan-Daten
// (GET /api/v2/subscription/plans, dieselbe Quelle wie planQuotas.ts in
// PlansAndApiPage) statt aus dem Text — eine Rekalibrierung schlägt damit
// automatisch durch. 'unlimited'/'unknown' fällt auf die Fallback-Matrix
// zurück (für 'free' dort ohnehin numerisch hinterlegt).
// eslint-disable-next-line react-refresh/only-export-components -- reine Helper-Funktion, gemeinsam mit den Karten genutzt (SubscriptionPage + CancelCard).
export function freeTierQuotaNumber(quota: PlanQuota, fallback: number): number {
  return typeof quota === 'number' ? quota : fallback
}

export function CancelCard({
  subscription,
  freePlan,
  isMutating,
  onOpenCancelFlow,
  onResume,
  onSuccess,
  onError,
}: {
  subscription: Subscription
  freePlan: Plan | null
  isMutating: boolean
  onOpenCancelFlow: () => void
  onResume: () => Promise<void>
  onSuccess: (message: string) => void
  onError: (message: string) => void
}) {
  const { t } = useTranslation('subscription')
  const periodEnd = formatSubscriptionDate(subscription.current_period_end)
  const freeQuotas = freePlan ? planQuotas(freePlan) : PLAN_QUOTA_FALLBACK.free
  const freeMobile = freeTierQuotaNumber(
    freeQuotas.mobile,
    PLAN_QUOTA_FALLBACK.free.mobile as number,
  )
  const freeIot = freeTierQuotaNumber(freeQuotas.iot, PLAN_QUOTA_FALLBACK.free.iot as number)

  async function handleResume() {
    try {
      await onResume()
      onSuccess(t('page.toast.resumeSuccess'))
    } catch {
      onError(t('cancelCard.resumeError'))
    }
  }

  return (
    <div className="card card-pad" data-testid="cancel-section">
      <h3 style={{ margin: '0 0 8px', fontSize: 14 }}>{t('cancelCard.title')}</h3>

      {subscription.cancel_at_period_end ? (
        <>
          <div className="hint" data-testid="cancel-resume-hint">
            {t('cancelCard.resumeHint', { date: periodEnd })}
          </div>
          <DemoGuardedButton
            className="btn"
            style={{ marginTop: 10 }}
            onClick={() => { void handleResume() }}
            disabled={isMutating}
            data-testid="resume-btn"
          >
            {t('cancelCard.resumeButton')}
          </DemoGuardedButton>
        </>
      ) : (
        <>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 10 }}>
            {t('cancelCard.description', { mobile: freeMobile, iot: freeIot })}
          </div>
          <DemoGuardedButton
            className="btn"
            style={{ color: 'var(--danger)', borderColor: 'color-mix(in oklch, var(--danger) 35%, var(--border))' }}
            onClick={onOpenCancelFlow}
            data-testid="cancel-btn"
          >
            {t('cancelCard.button')}
          </DemoGuardedButton>
        </>
      )}
    </div>
  )
}
